import React, { useState, useEffect, useCallback } from 'react';
import { Col, Container, Row, Input, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Button, Label } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Edit, Info as InfoIcon, UserPlus, UserX, User } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { fetchTableData, fetchFormData, requestNewTask, assignToSelf, releaseFromSelf } from '../../../Api/ApiMethods';
import PatchCallFormWorkInfoModal from './PatchCallFormWorkInfoModal';
import { Btn } from '../../../AbstractElements';
import { toast } from 'react-toastify';

// Rol bazlı state filtreleme konfigürasyonu
const roleStateFilterConfig = {
    'CALL CENTER': ['Düzeltme Bekleniyor'],
    'HEAD CALL CENTER': ['Düzeltme Bekleniyor'],
    'APPRAISER': ['Eksper Onay Bekleniyor', 'Eksper İşlem Bekleniyor', 'Eksper Rapor Bekleniyor'],
    'HEAD APPRAISER': ['Eksper Onay Bekleniyor', 'Eksper İşlem Bekleniyor', 'Eksper Rapor Bekleniyor'],
    'ACCOUNTING': ['Eksper Raporu Ödeme Bekleniyor'], // Eksper Ödeme Bekleniyor yerine Eksper Raporu Ödeme Bekleniyor varsayıldı
    'HEAD ACCOUNTING': ['Ödeme Bekleniyor', 'Eksper Raporu Ödeme Bekleniyor'], // Eksper Ödeme Bekleniyor yerine Eksper Raporu Ödeme Bekleniyor varsayıldı
    'POWER OF ATTORNEY REGISTRATION': ['Vekalet Bekleniyor'],
    'DOCUMENT REQUEST': ['Evrak Bekleniyor'],
    'INSURANCE': ['Sigorta Başvuru Dilekçe Bekleniyor'],
};

// Çalışılmayanlar sekmesi için durumlar (Tab 6)
const inactiveStatusesFilter = ['Müvekkil Reddedildi', 'Müşteri Vazgeçti', 'Çağrı Formu Reddedildi'];

const TodoListListing = ({ onCountChange, activeTabId }) => {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [callFormStates, setCallFormStates] = useState({});
    const [callFormStatesList, setCallFormStatesList] = useState([]);
    const [selectedStateFilter, setSelectedStateFilter] = useState('');
    const [userRole, setUserRole] = useState(localStorage.getItem('userRole')); // Dynamic user role state
    const navigate = useNavigate();

    const [isPatchModalOpen, setIsPatchModalOpen] = useState(false);
    const [currentPatchCallFormId, setCurrentPatchCallFormId] = useState(null);
    const [currentPatchInitialData, setCurrentPatchInitialData] = useState(null);

    const [isAssigningTask, setIsAssigningTask] = useState(false);
    const [isReleasingTask, setIsReleasingTask] = useState(false);
    const [isRemovingAssignment, setIsRemovingAssignment] = useState(false);

    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [jobToAssignId, setJobToAssignId] = useState(null);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);

    const callFormApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callform';
    const callFormStateApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callformstate';

    // Reset state filter when tab changes
    useEffect(() => {
        setSelectedStateFilter(''); // Reset filter when tab changes
        setPage(1); // Reset to first page
    }, [activeTabId]);

    // Effect to listen for role changes
    useEffect(() => {
        const handleRoleChange = () => {
            const newUserRole = localStorage.getItem('userRole');
            setUserRole(newUserRole);
            
            // Reset filters and reload data when role changes
            setSelectedStateFilter('');
            setPage(1);
            
            console.log('TodoListListing: Role changed to', newUserRole);
        };

        // Listen for role change events
        window.addEventListener('userRoleChanged', handleRoleChange);

        return () => {
            window.removeEventListener('userRoleChanged', handleRoleChange);
        };
    }, []);

    useEffect(() => {
        const fetchStates = async () => {
            console.log("Fetching call form states...");
            try {
                const response = await fetchFormData({
                    apiEndpoint: callFormStateApiUrl,
                    page: 1,
                    pageSize: 100 // Fetch all states
                });
                console.log("API Response for states:", response);

                let allItems = [];
                if (response && response.data && Array.isArray(response.data.items)) {
                    allItems = response.data.items;
                } else if (response && Array.isArray(response.items)) {
                    allItems = response.items;
                } else if (Array.isArray(response)) {
                    allItems = response;
                }

                if (allItems.length > 0) {
                    console.log("Total states fetched from API:", allItems.length);
                    const stateMap = {};
                    allItems.forEach(item => {
                        if (item && item.id && item.title) {
                            stateMap[item.id] = item.title;
                        }
                    });
                    setCallFormStates(stateMap);

                    const allowedTitles = roleStateFilterConfig[userRole];
                    const showAllStates = !allowedTitles || userRole === 'COORDINATOR' || userRole === 'ADMIN';

                    console.log(`Filtering states for Role: ${userRole}`); // Log: Rol
                    console.log("Allowed Titles by Role:", allowedTitles); // Log: İzin verilen başlıklar
                    console.log("Show All States Flag:", showAllStates); // Log: Tümünü gösterme bayrağı

                    const validItems = allItems.filter(item => item && item.id && item.title); // Sadece geçerli item'lar
                    console.log("Valid items before role filter:", validItems);

                    let filteredItems;
                    
                    // Çalışılmayanlar sekmesi (Tab 6) için özel filtreleme
                    if (activeTabId === '6') {
                        filteredItems = validItems.filter(item => 
                            inactiveStatusesFilter.includes(item.title)
                        );
                        console.log("Items filtered for inactive status (Tab 6):", filteredItems);
                    } else {
                        // Diğer sekmeler için mevcut rol bazlı filtreleme
                        filteredItems = validItems.filter(item => {
                            const isAllowed = showAllStates || (allowedTitles && allowedTitles.includes(item.title));
                            // Log each item and whether it's allowed
                            // console.log(`Item: ${item.title}, Allowed: ${isAllowed}`); // Detaylı log için açılabilir
                            return isAllowed;
                        });
                        console.log("Items after role filter:", filteredItems);
                    }

                    const statesForDropdown = filteredItems.map(item => ({ id: item.id, title: item.title }));

                    console.log(`Final states for dropdown (Count: ${statesForDropdown.length}):`, statesForDropdown);
                    setCallFormStatesList(statesForDropdown);

                } else {
                    console.warn('No valid call form state items found in response:', response);
                    setCallFormStates({});
                    setCallFormStatesList([]);
                }
            } catch (error) {
                console.error('Error fetching call form states:', error);
                setCallFormStates({});
                setCallFormStatesList([]);
            }
        };
        
        if (userRole) {
            fetchStates();
        }
    }, [callFormStateApiUrl, userRole, activeTabId]);

    useEffect(() => {
        console.log("callFormStatesList state updated:", callFormStatesList);
        
        // Çalışılmayanlar sekmesi (Tab 6) için "Müvekkil Reddedildi" otomatik seçimi
        if (activeTabId === '6' && callFormStatesList.length > 0 && !selectedStateFilter) {
            const muvekkilReddedildiState = callFormStatesList.find(state => 
                state.title === 'Müvekkil Reddedildi'
            );
            if (muvekkilReddedildiState) {
                setSelectedStateFilter(muvekkilReddedildiState.id);
            }
        }
    }, [callFormStatesList, activeTabId, selectedStateFilter]);

    const fetchData = useCallback(async () => {
        // Çalışılmayanlar sekmesi (Tab 6) için selectedStateFilter kontrolü
        if (activeTabId === '6' && (!selectedStateFilter || selectedStateFilter === '')) {
            setLoading(false);
            setData([]);
            setTotalRows(0);
            if (onCountChange) {
                onCountChange(0);
            }
            return; // Don't fetch data until a state is selected for Tab 6
        }

        setLoading(true);
        let apiEndpoint = callFormApiUrl;
        const queryParams = new URLSearchParams();

        // Çalışılmayanlar sekmesi (Tab 6) için filtreleme
        if (activeTabId === '6') {
            // Çalışılmayanlar sekmesinde mutlaka bir durum seçilmelidir
            if (selectedStateFilter && selectedStateFilter !== '') {
                queryParams.append('callformstate', selectedStateFilter);
            }
        } else {
            // Diğer sekmeler için mevcut filtreleme mantığı
            if (selectedStateFilter && selectedStateFilter !== 'all') {
                queryParams.append('callformstate', selectedStateFilter);
            }
        }

        if (search) {
            queryParams.append('search', search);
        }

        const queryString = queryParams.toString();
        if (queryString) {
            apiEndpoint += `?${queryString}`;
        }

        console.log("Fetching data from endpoint:", apiEndpoint);

        try {
            await fetchTableData({
                apiEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: setData,
                setTotalRowsCallback: (newTotalRows) => {
                    setTotalRows(newTotalRows);
                    if (onCountChange) {
                        onCountChange(newTotalRows);
                    }
                },
            });
        } catch (error) {
            console.error("Error in fetchTableData from TodoListListing:", error);
            setData([]);
            setTotalRows(0);
            if (onCountChange) {
                onCountChange(0);
            }
        } finally {
            setLoading(false);
        }
    }, [callFormApiUrl, page, pageSize, search, selectedStateFilter, onCountChange, activeTabId]); // callFormStates dependency'sini çıkardık

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
        setPage(1);
    };

    const handleStateFilterChange = (event) => {
        setSelectedStateFilter(event.target.value);
        setPage(1);
    };

    const handleEdit = (id) => {
        navigate(`/callform/${id}`);
    };

    const openPatchModal = (row) => {
        if (row && row.id) {
            const accidentData = row.accidents && row.accidents.length > 0 ? row.accidents[0] : {};

            setCurrentPatchCallFormId(row.id);
            setCurrentPatchInitialData({
                isTrialDocument: accidentData.isTrialDocument,
                bonusEligibility: accidentData.bonusEligibility, // API'den gelen 'bonusEligibility' alanını kullanıyoruz
            });
            setIsPatchModalOpen(true);
        } else {
            console.error("Cannot open patch modal: row data or ID is missing.", row);
        }
    };

    const togglePatchModal = () => {
        setIsPatchModalOpen(!isPatchModalOpen);
        if (isPatchModalOpen) { // if closing
            setCurrentPatchCallFormId(null);
            setCurrentPatchInitialData(null);
        }
    };

    const fetchEmployees = async (userRoleFilter) => {
        try {
            const response = await fetch(process.env.REACT_APP_LACIVERT_API_ENDPOINT + `/user?userRole=${userRoleFilter}`);
            const data = await response.json();
            let users = [];
            
            if (data?.isSuccess && Array.isArray(data.data?.items)) {
                users = data.data.items;
            } else if (Array.isArray(data?.data)) {
                users = data.data;
            } else if (Array.isArray(data)) {
                users = data;
            }

            const formattedUsers = users
                .filter(user => user.id && user.firstName && user.lastName)
                .map(user => ({
                    id: user.id,
                    name: `${user.firstName} ${user.lastName}`
                }));

            setEmployeeList(formattedUsers);
        } catch (error) {
            console.error("Error fetching employees:", error);
            toast.error("Personel listesi alınırken bir hata oluştu.");
            setEmployeeList([]);
        }
    };

    const toggleAssignModal = (jobId = null) => {
        if (jobId) {
            setSelectedUserId('');
            setJobToAssignId(jobId);
            
            // Atanacak iş kaydını bul ve durumuna göre uygun personel listesini çek
            const jobRecord = data.find(item => item.id === jobId);
            if (jobRecord) {
                const stateObject = Array.isArray(jobRecord.callFormState) ? jobRecord.callFormState[0] : jobRecord.callFormState;
                const stateTitle = stateObject?.title || '';
                
                // Duruma göre rol filtresi belirle
                let userRoleFilter = 'appraiser'; // varsayılan
                if (stateTitle === 'Eksper Onay Bekleniyor') {
                    userRoleFilter = 'appraiser';
                } else if (stateTitle === 'Evrak Bekleniyor') {
                    userRoleFilter = 'documentrequest';
                }
                
                // Uygun personel listesini çek
                fetchEmployees(userRoleFilter);
            }
        } else {
            setJobToAssignId(null);
        }
        setAssignModalOpen(!assignModalOpen);
    };

    const handleAssignUser = async () => {
        if (!jobToAssignId) {
            toast.error("Atanacak iş ID bulunamadı.");
            return;
        }
        if (!selectedUserId) {
            toast.error("Lütfen atanacak bir personel seçin.");
            return;
        }

        const assignEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/callform/update-assignment/${jobToAssignId}`;
        const payload = { userId: selectedUserId };
        setIsAssigning(true);

        try {
            const response = await fetch(assignEndpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const responseData = await response.json();

            if (responseData && responseData.isSuccess !== false) {
                toast.success('İş başarıyla personele atandı!');
                fetchData();
                toggleAssignModal();
            } else {
                const errorMessage = responseData?.errors?.userId?.[0] || responseData?.message || "İş atanırken bir hata oluştu.";
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error("Error assigning user:", error);
            toast.error("İş atanırken bir hata oluştu.");
        } finally {
            setIsAssigning(false);
        }
    };

    const handleRemoveAssignment = async (callFormId) => {
        if (isRemovingAssignment) return;
        
        const removeAssignApiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/callform/reset-task/${callFormId}`;
        if (!removeAssignApiEndpoint) {
            toast.error("Remove assignment API endpoint is not configured.");
            return;
        }

        setIsRemovingAssignment(true);
        try {
            const response = await fetch(removeAssignApiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            const responseData = await response.json();

            if (responseData && responseData.isSuccess !== false) {
                toast.success(responseData.message || "İş ataması başarıyla kaldırıldı!");
                fetchData(); // Listeyi yenile
            } else {
                toast.error(responseData?.message || "İş ataması kaldırılırken bir hata oluştu.");
            }
        } catch (error) {
            console.error("Error during handleRemoveAssignment:", error);
            toast.error("İş ataması kaldırılırken bir hata oluştu.");
        } finally {
            setIsRemovingAssignment(false);
        }
    };

    const handleAssignToSelf = async (callFormId) => {
        if (isAssigningTask) return;
        
        setIsAssigningTask(true);
        try {
            const apiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/callForm/assign-task-to-user`;
            
            const responseData = await assignToSelf({
                apiEndpoint,
                callFormId
            });

            toast.success(responseData?.message || "İş başarıyla kendinize atandı!");
            fetchData(); // Listeyi yenile
            
        } catch (error) {
            console.error("Error during handleAssignToSelf:", error);
            // Error toast is already handled by the API interceptor for most cases
            // Only show generic toast if it's not a server error
            if (!error.response || error.response.status >= 500) {
                toast.error("İş kendinize atanırken bir hata oluştu.");
            }
        } finally {
            setIsAssigningTask(false);
        }
    };

    const handleReleaseFromSelf = async (callFormId) => {
        if (isReleasingTask) return;
        
        setIsReleasingTask(true);
        try {
            const apiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/callForm/release-task-from-user`;
            
            const responseData = await releaseFromSelf({
                apiEndpoint,
                callFormId
            });

            toast.success(responseData?.message || "İş başarıyla kendinizden kaldırıldı!");
            fetchData(); // Listeyi yenile
            
        } catch (error) {
            console.error("Error during handleReleaseFromSelf:", error);
            // Error toast is already handled by the API interceptor for most cases
            // Only show generic toast if it's not a server error
            if (!error.response || error.response.status >= 500) {
                toast.error("İş kendinizden kaldırılırken bir hata oluştu.");
            }
        } finally {
            setIsReleasingTask(false);
        }
    };

    const columns = [
        {
            name: 'Çağrı Formu No',
            selector: row => row.callFormNumber || '-',
            sortable: true,
            width: '150px'
        },
        {
            name: 'Müşteri',
            selector: row => {
                const customer = row.customers?.[0];
                return customer?.clientName || customer?.companyName || '-';
            },
            sortable: true,
            grow: 2
        },
        {
            name: 'Plaka',
            selector: row => row.licensePlates?.[0]?.licensePlateNumber || '-',
            sortable: true,
            width: '130px'
        },
        {
            name: 'Vekaletname No',
            selector: row => row.powerOfAttorneys?.[0]?.poaNumber || '-',
            sortable: true,
            width: '160px'
        },
        {
            name: 'Kaza Dosya No',
            selector: row => row.accidentFiles?.[0]?.fileNumber || '-',
            sortable: true,
            width: '150px'
        },
        {
            name: 'Durum',
            selector: row => {
                const stateObject = Array.isArray(row.callFormState) ? row.callFormState[0] : row.callFormState;
                const stateIdValue = (typeof stateObject === 'object' && stateObject !== null) ? stateObject.id : stateObject;

                // Display için tüm state'lerin haritasını kullan
                if (stateIdValue) {
                    return callFormStates[stateIdValue] || stateIdValue;
                }
                return '-';
            },
            sortable: true,
            grow: 1.5
        },
                {
            name: 'Düzenle',
            cell: (row) => {                
                return (
                    <div
                        className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                        onClick={() => handleEdit(row.id)}
                        title="Düzenle"
                    >
                        <Edit size={18} />
                    </div>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '100px'
        },
        {
            name: 'Kendine Ata/Kaldır',
            cell: (row) => {                
                // Eğer appointed user yoksa, kendine atama butonu göster
                if (!row.appointedUser) {
                    return (
                        <div
                            className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                            onClick={() => handleAssignToSelf(row.id)}
                            title="Kendine Ata"
                            disabled={isAssigningTask}
                        >
                            <UserPlus size={18} />
                        </div>
                    );
                }
                
                // Eğer appointed user mevcut kullanıcı ise ve canRelease true ise, kendinden kaldırma butonu göster
                if (row.canRelease) {
                    // Özel koşullar: Belirli rol ve statü kombinasyonlarında kaldırma butonu gösterilmez
                    const stateObject = Array.isArray(row.callFormState) ? row.callFormState[0] : row.callFormState;
                    const stateTitle = stateObject?.title || '';
                    
                    // APPRAISER rolünde "Eksper Onay Bekleniyor" statüsünde kaldırma butonu gösterilmez
                    if (userRole === 'APPRAISER' && stateTitle === 'Eksper Onay Bekleniyor') {
                        return null;
                    }
                    
                    // DOCUMENT REQUEST rolünde "Evrak Bekleniyor" statüsünde kaldırma butonu gösterilmez
                    if (userRole === 'DOCUMENT REQUEST' && stateTitle === 'Evrak Bekleniyor') {
                        return null;
                    }
                    
                    return (
                        <div
                            className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                            onClick={() => handleReleaseFromSelf(row.id)}
                            title="Kendinden Kaldır"
                            disabled={isReleasingTask}
                        >
                            <UserX size={18} />
                        </div>
                    );
                }
                
                // Başka bir kullanıcıya atanmış ise veya canRelease false ise hiçbir buton gösterme
                return null;
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '150px',
            right: true,
        },
    ];

    // Atama butonlarını sadece belirli roller için ekle
    if (userRole === 'COORDINATOR' || userRole === 'MANAGEMENT' || userRole === 'HEAD APPRAISER') {
        columns.push(
            {
                name: 'İş Ata/Kaldır',
                cell: (row) => {
                    const stateObject = Array.isArray(row.callFormState) ? row.callFormState[0] : row.callFormState;
                    const stateTitle = stateObject?.title || '';
                    
                    // Koşul 1: Eksper Onay Bekleniyor - tüm roller (COORDINATOR, MANAGEMENT, HEAD APPRAISER)
                    const isEksperOnayBekleniyor = stateTitle === 'Eksper Onay Bekleniyor';
                    
                    // Koşul 2: Evrak Bekleniyor - sadece COORDINATOR ve MANAGEMENT
                    const isEvrakBekleniyor = stateTitle === 'Evrak Bekleniyor' && 
                                            (userRole === 'COORDINATOR' || userRole === 'MANAGEMENT');

                    // Her iki koşuldan biri sağlanmalı
                    if (!isEksperOnayBekleniyor && !isEvrakBekleniyor) return null;

                    // Eğer atanmış kullanıcı yoksa, atama butonu göster
                    if (!row.appointedUser) {
                        return (
                            <div
                                className="btn btn-outline-success d-flex align-items-center justify-content-center"
                                onClick={() => toggleAssignModal(row.id)}
                                disabled={isAssigning}
                                title="İş Ata"
                            >
                                <UserPlus size={18} />
                            </div>
                        );
                    }

                    // Eğer atanmış kullanıcı varsa, kaldırma butonu göster
                    return (
                        <div
                            className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                            onClick={() => handleRemoveAssignment(row.id)}
                            disabled={isRemovingAssignment}
                            title="İş Atamasını Kaldır"
                        >
                            <UserX size={18} />
                        </div>
                    );
                },
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
                width: '150px'
            }
        );
    }

    // Dosya Bilgisi Güncelle sütununu ekle
    if (userRole === 'COORDINATOR' || userRole === 'MANAGEMENT') {
        columns.push({
            name: 'Bilgi Güncelle',
            cell: (row) => {
                return (
                    <div
                        className="btn btn-outline-info d-flex align-items-center justify-content-center"
                        onClick={() => openPatchModal(row)}
                        title="Dosya Bilgisi"
                    >
                        <InfoIcon size={18} />
                    </div>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '180px'
        });
    }

    // Sekme değiştiğinde filtreleri sıfırla
    useEffect(() => {
        setSelectedStateFilter('');
        setSearch('');
        setPage(1);
    }, [activeTabId]);

    return (
        <Container fluid>
            <Row>
                <Col sm="12">
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="Çağrı formu no, müşteri adı vb. ile ara..."
                                    value={search}
                                    onChange={handleSearchChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>Durum Filtrele</Label>
                                <Input
                                    type="select"
                                    value={selectedStateFilter}
                                    onChange={handleStateFilterChange}
                                >
                                    {activeTabId !== '6' && (
                                        <option value="">Tümü</option>
                                    )}
                                    {callFormStatesList.map(state => (
                                        <option key={state.id} value={state.id}>
                                            {state.title}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <DataTable
                        columns={columns}
                        data={data}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={(newPageSize) => {
                            setPageSize(newPageSize);
                            setPage(1);
                        }}
                        onChangePage={setPage}
                        progressPending={loading}
                        highlightOnHover
                        pointerOnHover
                        responsive
                        striped
                        noDataComponent="Veri bulunamadı"
                    />

                    {/* Personel Atama Modal */}
                    <Modal isOpen={assignModalOpen} toggle={() => toggleAssignModal()}>
                        <ModalHeader toggle={() => toggleAssignModal()}>
                            Personel Ata
                        </ModalHeader>
                        <ModalBody>
                            <FormGroup>
                                <Label for="userSelect">Personel Seçin</Label>
                                <Input
                                    type="select"
                                    id="userSelect"
                                    value={selectedUserId}
                                    onChange={(e) => setSelectedUserId(e.target.value)}
                                >
                                    <option value="">Personel seçin...</option>
                                    {employeeList.map(employee => (
                                        <option key={employee.id} value={employee.id}>
                                            {employee.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={() => toggleAssignModal()}>
                                İptal
                            </Button>
                            <Button 
                                color="primary" 
                                onClick={handleAssignUser}
                                disabled={isAssigning || !selectedUserId}
                            >
                                {isAssigning ? 'Atanıyor...' : 'Ata'}
                            </Button>
                        </ModalFooter>
                    </Modal>

                    {/* Patch Modal */}
                    <PatchCallFormWorkInfoModal
                        isOpen={isPatchModalOpen}
                        toggle={togglePatchModal}
                        callFormId={currentPatchCallFormId}
                        initialData={currentPatchInitialData}
                        onSuccess={() => {
                            fetchData();
                            togglePatchModal();
                        }}
                    />
                </Col>
            </Row>
        </Container>
    );
};

export default TodoListListing;
